import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { useAuth } from "@/lib/AuthContext";
import { DOCS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  UploadCloud,
  FileText,
  Download,
  Trash2,
  Pencil,
  Award,
  CalendarClock,
  Building2,
  Search,
  X,
  RefreshCw,
  User as UserIcon,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = {
  manual: {
    label: "Manuale",
    icon: UploadCloud,
    cls: "bg-slate-100 text-slate-700 border-slate-200",
  },
  deadline_attachment: {
    label: "Da scadenza",
    icon: CalendarClock,
    cls: "bg-sky-100 text-sky-800 border-sky-200",
  },
  attestati_import: {
    label: "Da attestato",
    icon: Award,
    cls: "bg-amber-100 text-amber-800 border-amber-200",
  },
};

function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || SOURCE_META.manual;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.cls}`}
    >
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

export default function DocumentsPage() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === "super_admin";

  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [filterCompany, setFilterCompany] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [filterWorker, setFilterWorker] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", name: "", category: "generale", worker_id: "" });
  const formCompanyWorkers = useMemo(
    () => workers.filter((w) => w.company_id === form.company_id),
    [workers, form.company_id]
  );
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", category: "" });
  const [editFile, setEditFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteDoc, setDeleteDoc] = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);

  const load = useCallback(() => {
    const q = filterCompany !== "all" ? `?company_id=${filterCompany}` : "";
    api.get(`/documents${q}`).then((r) => setList(r.data));
  }, [filterCompany]);
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(Array.isArray(r.data) ? r.data : r.data?.items || []));
  }, []);
  // Ricarica workers quando cambia il filtro azienda per limitare la lista al contesto
  useEffect(() => {
    const q = filterCompany !== "all" ? `?company_id=${filterCompany}` : "";
    api.get(`/workers${q}`).then((r) => setWorkers(Array.isArray(r.data) ? r.data : []));
  }, [filterCompany]);
  useEffect(() => {
    load();
  }, [load]);
  // Reset filtro lavoratore se cambia azienda e il lavoratore non appartiene più
  useEffect(() => {
    if (filterWorker !== "all" && !workers.find((w) => w.id === filterWorker)) {
      setFilterWorker("all");
    }
  }, [workers, filterWorker]);

  const companyName = useMemo(() => {
    const m = new Map();
    companies.forEach((c) => m.set(c.id, c.name));
    return (id) => (id ? m.get(id) || "—" : "—");
  }, [companies]);

  const workerName = useMemo(() => {
    const m = new Map();
    workers.forEach((w) => m.set(w.id, `${w.first_name || ""} ${w.last_name || ""}`.trim() || w.tax_code || "—"));
    return (id) => (id ? m.get(id) || null : null);
  }, [workers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((d) => {
      if (filterSource !== "all" && (d.source || "manual") !== filterSource) return false;
      if (filterWorker !== "all" && d.worker_id !== filterWorker) return false;
      if (!q) return true;
      const wn = workerName(d.worker_id) || "";
      const hay = `${d.name || ""} ${d.filename || ""} ${d.category || ""} ${companyName(d.company_id)} ${wn}`.toLowerCase();
      return hay.includes(q);
    });
  }, [list, search, filterSource, filterWorker, companyName, workerName]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Seleziona un file");
      return;
    }
    const fd = new FormData();
    fd.append("company_id", form.company_id);
    fd.append("name", form.name || file.name);
    fd.append("category", form.category);
    if (form.worker_id) fd.append("worker_id", form.worker_id);
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOpen(false);
      setFile(null);
      setForm({ company_id: "", name: "", category: "generale", worker_id: "" });
      toast.success(t("save"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) {
      setFile(f);
      setOpen(true);
    }
  };

  const openEdit = (d) => {
    setEditDoc(d);
    setEditForm({ name: d.name || "", category: d.category || "generale" });
    setEditFile(null);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editDoc) return;
    if (!editForm.name.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("name", editForm.name.trim());
    fd.append("category", editForm.category.trim() || "generale");
    if (editFile) fd.append("file", editFile);
    try {
      await api.patch(`/documents/${editDoc.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento aggiornato");
      setEditDoc(null);
      setEditFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    }
    setSaving(false);
  };

  const confirmDelete = async () => {
    if (!deleteDoc) return;
    try {
      await api.delete(`/documents/${deleteDoc.id}`);
      toast.success(t("delete"));
      setDeleteDoc(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
      setDeleteDoc(null);
    }
  };

  const runBackfill = async () => {
    if (backfillRunning) return;
    if (!window.confirm(
      "Vuoi sincronizzare TUTTI gli allegati storici delle scadenze nella sezione Documenti?\n\n" +
      "L'operazione è idempotente: i documenti già presenti verranno saltati."
    )) return;
    setBackfillRunning(true);
    try {
      const r = await api.post("/documents/backfill");
      const d = r.data || {};
      toast.success(
        `Sincronizzazione completata: ${d.created} creati, ${d.skipped_already_existing} già presenti, ${d.scanned} scansionati.`
      );
      if (d.errors?.length) {
        toast.warning(`${d.errors.length} errori — vedi console per dettagli`);
        console.warn("Backfill errors:", d.errors);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore sincronizzazione");
    }
    setBackfillRunning(false);
  };

  // Lista workers filtrata per l'azienda selezionata (già gestita nel fetch)
  const workersForFilter = workers;

  return (
    <div data-testid={DOCS.root} className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("documents")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {filtered.length}
            {filtered.length !== list.length && ` / ${list.length}`} documenti · include allegati da
            scadenze e attestati
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, corso, lavoratore, azienda…"
              className="pl-9 pr-8 h-10 w-64"
              data-testid="docs-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="docs-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-40 h-10" data-testid="docs-source-filter">
              <SelectValue placeholder="Origine" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le origini</SelectItem>
              <SelectItem value="manual">Manuale</SelectItem>
              <SelectItem value="deadline_attachment">Da scadenza</SelectItem>
              <SelectItem value="attestati_import">Da attestato</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterCompany} onValueChange={setFilterCompany}>
            <SelectTrigger className="w-48 h-10">
              <SelectValue placeholder={t("company")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterWorker} onValueChange={setFilterWorker}>
            <SelectTrigger className="w-48 h-10" data-testid="docs-worker-filter">
              <SelectValue placeholder="Lavoratore" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i lavoratori</SelectItem>
              {workersForFilter.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  {(w.first_name || "") + " " + (w.last_name || "")}
                  {w.tax_code ? ` · ${w.tax_code}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isSuperAdmin && (
            <Button
              variant="outline"
              onClick={runBackfill}
              disabled={backfillRunning}
              className="rounded-full h-10 btn-lift"
              data-testid="docs-backfill"
              title="Sincronizza gli allegati storici delle scadenze nella sezione Documenti"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${backfillRunning ? "animate-spin" : ""}`} />
              {backfillRunning ? "Sincronizzazione…" : "Sincronizza storico"}
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={DOCS.uploadBtn} className="rounded-full btn-lift">
                <UploadCloud className="w-4 h-4 mr-1.5" /> {t("upload_document")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("upload_document")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>{t("company")}</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, worker_id: "" })}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("company")} />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("title")}</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={file?.name || "Es. DVR Generale, Verifica messa a terra…"}
                  />
                </div>
                <div>
                  <Label>{t("category")}</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                </div>
                {form.company_id && (
                  <div>
                    <Label>Lavoratore (opzionale)</Label>
                    <Select
                      value={form.worker_id || "__none__"}
                      onValueChange={(v) => setForm({ ...form, worker_id: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Nessuno" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Nessuno —</SelectItem>
                        {formCompanyWorkers.map((w) => (
                          <SelectItem key={w.id} value={w.id}>
                            {(w.first_name || "") + " " + (w.last_name || "")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>{t("file")}</Label>
                  <Input type="file" onChange={(e) => setFile(e.target.files?.[0])} />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-full btn-lift">
                    {t("save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div
        data-testid={DOCS.dropzone}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`dropzone rounded-lg py-10 text-center text-sm text-muted-foreground cursor-pointer ${
          dragActive ? "active" : ""
        }`}
      >
        <UploadCloud className="w-6 h-6 mx-auto mb-2 opacity-60" />
        {t("drop_here")}
        <input
          ref={inputRef}
          type="file"
          hidden
          onChange={(e) => {
            if (e.target.files?.[0]) {
              setFile(e.target.files[0]);
              setOpen(true);
            }
          }}
        />
      </div>

      <div className="bg-card border border-border rounded-lg divide-y divide-border">
        {filtered.map((d) => {
          const wn = workerName(d.worker_id);
          return (
            <div key={d.id} className="px-5 py-3 flex items-center gap-4">
              <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate flex items-center gap-2 flex-wrap">
                  <span className="truncate">{d.name}</span>
                  <SourceBadge source={d.source || "manual"} />
                  {d.version > 1 && (
                    <span className="text-[10px] uppercase tracking-widest bg-indigo-100 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5">
                      v{d.version}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {companyName(d.company_id)}
                  </span>
                  {wn && (
                    <>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1">
                        <UserIcon className="w-3 h-3" /> {wn}
                      </span>
                    </>
                  )}
                  <span>·</span>
                  <span>{d.category || "generale"}</span>
                  {d.size ? (
                    <>
                      <span>·</span>
                      <span>{(d.size / 1024).toFixed(1)} KB</span>
                    </>
                  ) : null}
                  {d.filename && d.filename !== d.name && (
                    <>
                      <span>·</span>
                      <span className="truncate max-w-[220px]">{d.filename}</span>
                    </>
                  )}
                </div>
              </div>
              <a
                href={`${BACKEND}${d.file_url}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs uppercase tracking-widest text-primary inline-flex items-center gap-1 btn-lift"
                data-testid={`docs-download-${d.id}`}
              >
                <Download className="w-3.5 h-3.5" /> {t("download")}
              </a>
              <button
                onClick={() => openEdit(d)}
                className="text-muted-foreground hover:text-primary btn-lift ml-2"
                data-testid={`docs-edit-${d.id}`}
                title="Modifica documento"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDeleteDoc(d)}
                data-testid={`delete-document-${d.id}`}
                className="text-muted-foreground hover:text-destructive btn-lift ml-1"
                title="Elimina documento"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground italic">
            {list.length === 0
              ? t("no_data")
              : "Nessun documento corrisponde ai filtri applicati."}
          </div>
        )}
      </div>

      <Dialog open={!!editDoc} onOpenChange={(o) => !o && setEditDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica documento</DialogTitle>
            <DialogDescription>
              Puoi cambiare nome, categoria e/o sostituire il file. Il vecchio file resta archiviato per
              lo storico.
            </DialogDescription>
          </DialogHeader>
          {editDoc && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div>
                <Label>Nome documento</Label>
                <Input
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="docs-edit-name"
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <Input
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  data-testid="docs-edit-category"
                />
              </div>
              <div>
                <Label>Sostituisci file (opzionale)</Label>
                <Input
                  type="file"
                  onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                  data-testid="docs-edit-file"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  File attuale: <b>{editDoc.filename || "—"}</b>
                  {editDoc.version > 1 && ` (versione ${editDoc.version})`}. Se carichi un nuovo file la
                  versione viene incrementata.
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setEditDoc(null)}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  className="rounded-full btn-lift"
                  disabled={saving}
                  data-testid="docs-edit-submit"
                >
                  {saving ? "Salvataggio…" : "Salva modifiche"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteDoc} onOpenChange={(o) => !o && setDeleteDoc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <b>{deleteDoc?.name}</b>. Il file resta nell'archivio ma non sarà
              più visibile nella sezione Documenti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="docs-delete-confirm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
